// admin/src/features/preview/PhoneScreens.tsx
//
// The app's screens, redrawn in points from their real layout values so a
// photo lands in the same box, crop and corner radius it gets on the
// phone. Sources: app/exercises/[id].tsx + ExerciseHeroCarousel.tsx
// (exercise page), app/exercises/pick.tsx + ExerciseThumb.tsx (picker
// row), src/features/train/TrainHome.tsx + builder/MyWorkouts.tsx (cover
// cards). Space/radius tokens: src/theme/spacing.ts.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { label } from '../../lib/vocab';
import type { DeviceSpec } from './devices';
import { Glyph, sportGlyph } from './icons';
import { coverGradient, muscleTint, type CoverSubject, type ExerciseSubject, type HeroItem } from './subject';

const GUTTER = 20;
const C = {
  bg: '#0B0C0F',
  surface: '#171A21',
  surfaceRaised: '#222631',
  border: '#2A2F3A',
  textPrimary: '#F5F6F8',
  textSecondary: '#9BA1AE',
  onAccent: '#0B0C0F',
};

function alpha(hex: string, a: number): string {
  const v = Math.round(a * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${v}`;
}

// ---------------------------------------------------------------------
// Chrome: status bar, cutout, home indicator
// ---------------------------------------------------------------------

function StatusBar({ device }: { device: DeviceSpec }) {
  const h = device.cutout === 'island' ? 54 : 47;
  return (
    <div className="pv-statusbar" style={{ height: h }}>
      <span className="pv-statusbar-time" style={{ width: device.cutout === 'island' ? 150 : 110 }}>
        9:41
      </span>
      {device.cutout === 'island' ? (
        <span className="pv-island" />
      ) : (
        <span className="pv-notch" />
      )}
      <span className="pv-statusbar-icons" style={{ width: device.cutout === 'island' ? 150 : 110 }}>
        <svg width="18" height="12" viewBox="0 0 18 12" fill="#fff" aria-hidden>
          <rect x="0" y="8" width="3" height="4" rx="1" />
          <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
          <rect x="10" y="3" width="3" height="9" rx="1" />
          <rect x="15" y="0" width="3" height="12" rx="1" />
        </svg>
        <svg width="16" height="12" viewBox="0 0 16 12" fill="#fff" aria-hidden>
          <path d="M8 2.4c2.3 0 4.4.9 6 2.4l1.2-1.2A10.2 10.2 0 0 0 8 .6C5.2.6 2.6 1.7.8 3.6L2 4.8a8.4 8.4 0 0 1 6-2.4Zm0 3.5c1.4 0 2.6.5 3.5 1.4l1.2-1.2A6.6 6.6 0 0 0 8 4.1c-1.8 0-3.5.7-4.7 2l1.2 1.2c.9-.9 2.1-1.4 3.5-1.4Zm0 3.4c-.5 0-1 .2-1.3.5L8 11.3l1.3-1.5c-.3-.3-.8-.5-1.3-.5Z" />
        </svg>
        <svg width="27" height="13" viewBox="0 0 27 13" aria-hidden>
          <rect x="0.5" y="0.5" width="23" height="12" rx="3.8" fill="none" stroke="#fff" strokeOpacity="0.4" />
          <rect x="2" y="2" width="20" height="9" rx="2.5" fill="#fff" />
          <path d="M25 4.5v4c.8-.3 1.4-1.1 1.4-2s-.6-1.7-1.4-2Z" fill="#fff" fillOpacity="0.4" />
        </svg>
      </span>
    </div>
  );
}

function PhoneShell({ device, children }: { device: DeviceSpec; children: ReactNode }) {
  return (
    <div className="pv-phone-screen" style={{ background: C.bg }}>
      <div className="pv-scroll">{children}</div>
      <StatusBar device={device} />
      <span className="pv-home-indicator" />
    </div>
  );
}

function NavButton() {
  return (
    <div style={{ height: 44, display: 'flex', alignItems: 'center', marginInlineStart: -8 }}>
      <div style={{ width: 44, height: 44, borderRadius: 22, background: C.surfaceRaised, display: 'grid', placeItems: 'center' }}>
        <Glyph name="X" size={22} color={C.textPrimary} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Exercise page
// ---------------------------------------------------------------------

function GradientTile({ tint, glyph, width, height, radius, glyphSize }: { tint: string; glyph: ReturnType<typeof sportGlyph>; width: number; height: number; radius: number; glyphSize: number }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: `linear-gradient(135deg, ${alpha(tint, 0.32)}, ${alpha(tint, 0.08)}), ${C.surfaceRaised}`,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
    >
      <Glyph name={glyph} size={glyphSize} color={tint} />
    </div>
  );
}

function HeroCarousel({ items, startIndex, width, height, tint, sport }: { items: HeroItem[]; startIndex: number; width: number; height: number; tint: string; sport: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    if (ref.current) ref.current.scrollLeft = startIndex * width;
    setIndex(startIndex);
  }, [startIndex, width]);

  if (items.length === 0) {
    return <GradientTile tint={tint} glyph={sportGlyph(sport)} width={width} height={height} radius={20} glyphSize={Math.round(height * 0.3)} />;
  }

  return (
    <div>
      <div
        ref={ref}
        className="pv-carousel"
        style={{ width, height, borderRadius: 20 }}
        onScroll={(e) => setIndex(Math.round(e.currentTarget.scrollLeft / width))}
      >
        {items.map((item) =>
          item.kind === 'photo' ? (
            <img key={item.key} src={item.url} alt="" style={{ width, height }} draggable={false} />
          ) : (
            <video key={item.key} src={item.url} poster={item.poster ?? undefined} style={{ width, height }} muted loop autoPlay playsInline />
          ),
        )}
      </div>
      {items.length > 1 ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 8 }}>
          {items.map((item, i) => (
            <span
              key={item.key}
              onClick={() => ref.current?.scrollTo({ left: i * width, behavior: 'smooth' })}
              style={{ width: i === index ? 16 : 6, height: 6, borderRadius: 3, background: i === index ? tint : C.border, cursor: 'pointer' }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Tag({ text, tint }: { text: string; tint: string }) {
  return (
    <span className="pv-caption" style={{ height: 28, borderRadius: 999, padding: '0 12px', display: 'inline-flex', alignItems: 'center', background: alpha(tint, 0.18), color: tint }}>
      {text}
    </span>
  );
}

const sectionLabel: CSSProperties = { marginTop: 24, marginBottom: 8, color: C.textSecondary };

export function ExercisePageScreen({ device, subject }: { device: DeviceSpec; subject: ExerciseSubject }) {
  const cardWidth = device.widthPt - GUTTER * 2;
  const heroHeight = Math.round(cardWidth * 0.86);
  const tint = muscleTint(subject.muscleGroup);
  const tags = [subject.muscleGroup, ...subject.muscles.filter((m) => m !== subject.muscleGroup)];

  return (
    <PhoneShell device={device}>
      <div style={{ padding: `${device.safeTopPt + 8}px ${GUTTER}px ${device.safeBottomPt + 40}px` }}>
        <NavButton />
        <HeroCarousel items={subject.items} startIndex={subject.startIndex} width={cardWidth} height={heroHeight} tint={tint} sport={subject.sport} />
        <div className="pv-hero-text" style={{ marginTop: 16 }}>
          {subject.name || 'Exercise'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {tags.map((m) => (
            <Tag key={m} text={label(m)} tint={tint} />
          ))}
        </div>
        {subject.equipment.length > 0 ? (
          <>
            <div className="pv-micro" style={sectionLabel}>
              Equipment
            </div>
            <div className="pv-hscroll" style={{ gap: 12, padding: '4px 0' }}>
              {subject.equipment.map((item) => (
                <div key={item.id} style={{ width: 64, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 56, height: 56, borderRadius: 16, overflow: 'hidden', background: C.surfaceRaised, display: 'grid', placeItems: 'center' }}>
                    {item.photoUrl ? <img src={item.photoUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover' }} /> : <Glyph name="Dumbbell" size={18} color={C.textSecondary} />}
                  </div>
                  <span className="pv-caption pv-oneline" style={{ color: C.textSecondary, textAlign: 'center', width: 64 }}>
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : null}
        {subject.description ? (
          <>
            <div className="pv-micro" style={sectionLabel}>
              Cues
            </div>
            <div className="pv-body" style={{ color: C.textPrimary, whiteSpace: 'pre-wrap' }}>
              {subject.description}
            </div>
          </>
        ) : null}
      </div>
    </PhoneShell>
  );
}

// ---------------------------------------------------------------------
// Exercise picker (the 64 pt list thumbnail)
// ---------------------------------------------------------------------

function ListThumb({ subject }: { subject: ExerciseSubject }) {
  const item = subject.items[subject.startIndex] ?? subject.items[0];
  const url = item ? (item.kind === 'photo' ? item.url : item.poster) : null;
  const tint = muscleTint(subject.muscleGroup);
  if (url) {
    return (
      <div style={{ width: 64, height: 64, borderRadius: 14, overflow: 'hidden', background: '#FFFFFF', flexShrink: 0 }}>
        <img src={url} alt="" style={{ width: 64, height: 64, objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div style={{ width: 64, height: 64, borderRadius: 14, background: alpha(tint, 0.16), display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <Glyph name={sportGlyph(subject.sport)} size={27} color={tint} />
    </div>
  );
}

function PickerRow({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 80, borderBottom: `0.5px solid ${C.border}` }}>{children}</div>;
}

export function ExerciseListScreen({ device, subject }: { device: DeviceSpec; subject: ExerciseSubject }) {
  return (
    <PhoneShell device={device}>
      <div style={{ padding: `${device.safeTopPt + 8}px ${GUTTER}px ${device.safeBottomPt + 40}px` }}>
        <NavButton />
        <div className="pv-display" style={{ marginTop: 12 }}>
          Add exercise
        </div>
        <div style={{ marginTop: 12, height: 44, borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface, display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', color: C.textSecondary }}>
          <Glyph name="Search" size={18} color={C.textSecondary} />
          <span style={{ fontSize: 16 }}>Search exercises</span>
        </div>
        <div style={{ marginTop: 16 }}>
          <PickerRow>
            <span style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${C.border}`, background: C.surface, flexShrink: 0 }} />
            <ListThumb subject={subject} />
            <span className="pv-body pv-oneline" style={{ flex: 1, color: C.textPrimary }}>
              {subject.name || 'Exercise'}
            </span>
            <Glyph name="ChevronRight" size={16} color={C.textSecondary} />
          </PickerRow>
          {[0.7, 0.5, 0.62].map((w, i) => (
            <PickerRow key={i}>
              <span style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${C.border}`, background: C.surface, flexShrink: 0 }} />
              <span style={{ width: 64, height: 64, borderRadius: 14, background: C.surface, flexShrink: 0 }} />
              <span style={{ height: 12, width: `${w * 60}%`, borderRadius: 6, background: C.surface }} />
            </PickerRow>
          ))}
        </div>
      </div>
    </PhoneShell>
  );
}

// ---------------------------------------------------------------------
// Train tab: the hero card and a My Workouts cover card
// ---------------------------------------------------------------------

function CoverArt({ subject }: { subject: CoverSubject }) {
  return (
    <>
      <img
        src={subject.url}
        alt=""
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${Math.round(subject.focalX * 100)}% ${Math.round(subject.focalY * 100)}%` }}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.68) 100%)' }} />
    </>
  );
}

export function CoverScreen({ device, subject }: { device: DeviceSpec; subject: CoverSubject }) {
  const [from, to] = coverGradient(subject.slot);
  const muted = 'rgba(255,255,255,0.82)';
  return (
    <PhoneShell device={device}>
      <div style={{ padding: `${device.safeTopPt + 8}px ${GUTTER}px ${device.safeBottomPt + 40}px` }}>
        <div className="pv-display" style={{ height: 48, display: 'flex', alignItems: 'center' }}>
          Train
        </div>
        <div style={{ position: 'relative', marginTop: 8, borderRadius: 20, overflow: 'hidden', padding: 20, minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <CoverArt subject={subject} />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: muted }}>
              <Glyph name="Dumbbell" size={13} color="#FFFFFF" />
              <span className="pv-micro">Today, 13 Oct</span>
            </div>
            <div className="pv-hero-text" style={{ marginTop: 8, color: '#FFFFFF' }}>
              Full body
            </div>
            <div className="pv-body" style={{ marginTop: 4, color: muted }}>
              45 min
            </div>
            <div
              className="pv-pill"
              style={{ marginTop: 20, height: 52, background: `linear-gradient(135deg, ${from}, ${to})`, color: C.onAccent }}
            >
              <Glyph name="Play" size={18} color={C.onAccent} />
              Start
            </div>
            <div className="pv-caption" style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: muted }}>
              <Glyph name="Watch" size={13} color={muted} />
              Syncs with your Apple Watch
            </div>
            <div className="pv-caption" style={{ marginTop: 4, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: muted, textDecoration: 'underline' }}>
              Log a past workout
            </div>
          </div>
        </div>

        <div className="pv-h2" style={{ marginTop: 24, marginBottom: 12, color: C.textPrimary }}>
          My workouts
        </div>
        <div className="pv-hscroll" style={{ gap: 12, marginInline: -GUTTER, paddingInline: GUTTER }}>
          {['Push day', 'Legs'].map((title) => (
            <div key={title} style={{ position: 'relative', width: 190, height: 168, flexShrink: 0, borderRadius: 20, overflow: 'hidden', padding: 12, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <CoverArt subject={subject} />
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#FFFFFF' }}>
                  <Glyph name="Dumbbell" size={13} color="#FFFFFF" />
                  <span className="pv-micro">Strength</span>
                </div>
                <div className="pv-title-text" style={{ color: '#FFFFFF', marginInlineEnd: 44 }}>
                  {title}
                </div>
                <div className="pv-caption" style={{ color: muted }}>
                  50 min
                </div>
              </div>
              <div style={{ position: 'absolute', right: 12, bottom: 12, width: 44, height: 44, borderRadius: 22, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center' }}>
                <Glyph name="Play" size={18} color="#FFFFFF" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </PhoneShell>
  );
}
